/**
 * Join Requests Section
 * 
 * Admin UI for managing join requests from users with authorised domains.
 * Shows pending requests and allows approval/rejection.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenancy, useCurrentUser } from '@/contexts/TenancyContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  UserPlus, 
  Check, 
  X, 
  Clock, 
  RefreshCw,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { JoinRequest, JoinRequestStatus } from '@/types/authorisedDomains';
import {
  getJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from '@/services/authDomainService';
import { format } from 'date-fns';

export function JoinRequestsSection() {
  const { activeTenancy } = useTenancy();
  const currentUser = useCurrentUser();
  const tenancyId = activeTenancy?.id;

  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'processed'>('pending');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const loadRequests = useCallback(async () => {
    if (!tenancyId) return;

    setIsLoading(true);
    try {
      const data = await getJoinRequests(tenancyId);
      setRequests(data);
    } catch (error) {
      console.error('[JoinRequestsSection] Error loading requests:', error);
      toast.error('Failed to load join requests');
    } finally {
      setIsLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async (request: JoinRequest) => {
    if (!tenancyId || !currentUser?.userID) return;

    setIsProcessing(true);
    try {
      const result = await approveJoinRequest(tenancyId, request.id, currentUser.userID);
      
      if (result.success) {
        setRequests(prev => prev.map(r => 
          r.id === request.id ? { ...r, status: 'approved' as JoinRequestStatus } : r
        ));
        toast.success(`${request.email} has been approved and added to the organization`);
      } else {
        toast.error(result.error || 'Failed to approve request');
      }
    } catch (error) {
      console.error('[JoinRequestsSection] Error approving request:', error);
      toast.error('Failed to approve request');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!tenancyId || !currentUser?.userID || !selectedRequest) return;

    setIsProcessing(true);
    try {
      const result = await rejectJoinRequest(
        tenancyId, 
        selectedRequest.id, 
        currentUser.userID,
        rejectionReason
      );
      
      if (result.success) {
        setRequests(prev => prev.map(r => 
          r.id === selectedRequest.id ? { ...r, status: 'rejected' as JoinRequestStatus } : r
        ));
        toast.success(`Request from ${selectedRequest.email} has been rejected`);
        setRejectDialogOpen(false);
        setSelectedRequest(null);
        setRejectionReason('');
      } else {
        toast.error(result.error || 'Failed to reject request');
      }
    } catch (error) {
      console.error('[JoinRequestsSection] Error rejecting request:', error);
      toast.error('Failed to reject request');
    } finally {
      setIsProcessing(false);
    }
  };

  const openRejectDialog = (request: JoinRequest) => {
    setSelectedRequest(request);
    setRejectDialogOpen(true);
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  const getStatusBadge = (status: JoinRequestStatus) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500"><Check className="mr-1 h-3 w-3" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><X className="mr-1 h-3 w-3" />Rejected</Badge>;
    }
  };

  if (!tenancyId) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Join Requests
            </CardTitle>
            <CardDescription>
              Review and manage requests from users to join your organization.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadRequests} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'processed')}>
          <TabsList className="mb-4">
            <TabsTrigger value="pending">
              Pending ({pendingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="processed">
              Processed ({processedRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : pendingRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No pending requests</h3>
                <p className="text-sm text-muted-foreground max-w-sm mt-2">
                  When users with authorised domains request to join, they'll appear here.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRequests.map(request => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.email}</TableCell>
                      <TableCell>{request.displayName || '-'}</TableCell>
                      <TableCell>{request.domain}</TableCell>
                      <TableCell>
                        {request.createdAt?.toDate 
                          ? format(request.createdAt.toDate(), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApprove(request)}
                            disabled={isProcessing}
                          >
                            <Check className="mr-1 h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRejectDialog(request)}
                            disabled={isProcessing}
                          >
                            <X className="mr-1 h-4 w-4" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="processed">
            {processedRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No processed requests</h3>
                <p className="text-sm text-muted-foreground max-w-sm mt-2">
                  Approved and rejected requests will appear here.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Processed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedRequests.map(request => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.email}</TableCell>
                      <TableCell>{request.domain}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell>
                        {request.reviewedAt?.toDate 
                          ? format(request.reviewedAt.toDate(), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent 
          className="sm:max-w-lg bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-card">
              <DialogHeader className="text-center">
                <DialogTitle className="text-2xl font-bold">Reject Join Request</DialogTitle>
                <DialogDescription className="mt-2">
                  Are you sure you want to reject the request from{' '}
                  <strong>{selectedRequest?.email}</strong>?
                </DialogDescription>
              </DialogHeader>
            </div>
            
            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto">
          <div className="space-y-4">
            <Label htmlFor="rejection-reason">Reason (optional)</Label>
            <Textarea
              id="rejection-reason"
              placeholder="Provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRejectDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                'Reject Request'
              )}
            </Button>
          </DialogFooter>
          </div>
            
            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card">
              <DialogFooter>
                <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleReject}>
                  {isProcessing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    'Reject Request'
                  )}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default JoinRequestsSection;
